import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/* Sparkle icon — same as Mira's */
function SparkleIcon({ size = 16, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" />
    </svg>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      {/* Hero */}
      <div style={{ background: 'var(--iab-cream)', paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <p className="uppercase mb-6" style={{ fontSize: 'var(--iab-text-xs)', letterSpacing: 'var(--iab-tracking-widest)', fontWeight: 'var(--iab-weight-semibold)', color: '#E91E8C' }}>
            about
          </p>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontFamily: 'var(--genz-font-display)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 'var(--iab-leading-tight)' }}>
            <span style={{ color: '#16130F' }}>iam</span><span style={{ color: '#C17F24' }}>because</span>
          </h1>
          <p className="mt-6 hand-note" style={{ fontSize: '26px', lineHeight: '1.5', color: '#E91E8C', maxWidth: '600px' }}>
            the deepest reason any of us acts is love, wearing the costume of work.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {/* Thin amber divider */}
        <div className="mb-12" style={{ height: '1px', background: 'var(--iab-border-amber)' }} />

        <div className="space-y-8" dir="auto">

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            There is a question nobody asks.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            Not your boss. Not your colleagues. Not the people who have known you for twenty years.
          </p>

          {/* The big question — Playfair italic, larger, amber */}
          <p style={{ fontFamily: 'var(--genz-font-display)', fontSize: 'var(--iab-text-story-lead)', fontWeight: 700, lineHeight: 'var(--iab-leading-relaxed)', color: '#16130F' }}>
            why do you do <span className="highlight-mark">what you do?</span>
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            Not how you got the job. Not what&rsquo;s on your LinkedIn. The real reason. The moment. The person. The circumstance that shaped everything that came after.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            <span style={{ fontWeight: 600 }}><span style={{ color: '#16130F' }}>iam</span><span style={{ color: '#C17F24' }}>because</span></span> exists because of that question.
          </p>

          {/* Long paragraph — blockquote style */}
          <blockquote style={{ borderLeft: '2px solid var(--iab-border-amber)', paddingLeft: '32px', margin: '40px 0' }}>
            <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal-soft)' }}>
              Since 1998 &mdash; through Les Concierges&reg;, Club Concierge&reg;, MindEscapes&reg; and The Doggy Company&reg; &mdash; we have been in rooms with some of the most extraordinary people in India. The urban professional who built a career to give her children choices she never had. The innovator who left a corner office because he needed to build something that mattered. The concierge who shows up every single day because she once watched someone&rsquo;s life become lighter simply because another person said <em style={{ fontStyle: 'italic' }}>I have it handled</em>. The pet parent who restructured their entire life around love for another creature. The member who has access to everything &mdash; and still can&rsquo;t fully explain why they do what they do.
            </p>
          </blockquote>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            The more people we served, the more we realised something.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal-soft)', fontStyle: 'italic' }}>
            Everyone has a reason. A moment. A person. A quiet truth behind the work. And almost nobody has ever been asked what it is.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            These stories exist everywhere. They just never get asked for.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            Some people arrive here with their why already written &mdash; clear, certain, hard-won. Others arrive still searching. Still unsatisfied. Still in the middle of becoming. Both belong here equally. The searching is also a story worth telling. And sometimes, in the telling, the answer finds you.
          </p>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            So we built a place for them.
          </p>

          {/* Centered manifesto lines — Playfair, each on own line */}
          <div className="text-center py-10" style={{ borderTop: '0.5px solid var(--iab-border)', borderBottom: '0.5px solid var(--iab-border)', margin: '48px 0' }}>
            <div className="space-y-1" style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-lg)', lineHeight: 'var(--iab-leading-relaxed)', color: 'var(--iab-charcoal)' }}>
              <p>one question.</p>
              <p>every human.</p>
              <p>no agenda.</p>
              <p>no revenue.</p>
              <p>no editing.</p>
            </div>
            <p className="mt-4" style={{ fontFamily: 'var(--genz-font-display)', fontSize: 'var(--iab-text-xl)', color: '#E91E8C', fontWeight: 700 }}>
              just why.
            </p>
          </div>

          {/* The Mirror aside */}
          <div className="py-6" style={{ maxWidth: '520px', margin: '0 auto' }}>
            <p style={{ fontFamily: 'var(--genz-font-hand)', fontSize: '19px', lineHeight: '1.7', color: 'var(--iab-charcoal)' }}>
              And for those who want to go further — there is The Mirror. A private conversation with Mira about your own year. Not for the world. Not for your manager. Just for you. One honest hour that stays entirely between you and her.
            </p>
            <p className="mt-4" style={{ fontFamily: 'var(--genz-font-hand)', fontSize: '19px', lineHeight: '1.7', color: 'var(--iab-charcoal)' }}>
              The why is the compass. The Mirror checks if you've been walking in the right direction.
            </p>
          </div>

          {/* Icons of India — the legacy room */}
          <div className="my-14 p-8 md:p-10" style={{ background: 'linear-gradient(135deg, #1A1A18 0%, #2A2520 100%)', borderRadius: '16px' }}>
            <p className="uppercase mb-5" style={{ fontSize: 'var(--iab-text-xs)', letterSpacing: 'var(--iab-tracking-widest)', fontWeight: 'var(--iab-weight-semibold)', color: '#C99A2E' }}>
              The room of those who came before
            </p>
            <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'rgba(254,252,247,0.85)' }}>
              Long before iambecause had a name, MindEscapes&reg; was already asking the question &mdash; to the people who built modern India. Sam Pitroda, the man who connected a nation, sat down with twenty-one of its architects: the scientist who launched the first satellite, the woman who built biotech when they said she couldn&rsquo;t, the economist who turned numbers into justice, the artist who painted because silence was never an option.
            </p>
            <p className="mt-5" style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-md)', lineHeight: 'var(--iab-leading-story)', color: 'rgba(254,252,247,0.85)' }}>
              Their answers now live here, in a legacy room we call <span style={{ color: '#C99A2E', fontWeight: 500 }}>Icons of India</span>. Because the question is the same at every scale. Whether you serve one family or a billion people &mdash; there was a moment, a person, a reason.
            </p>
            <p className="mt-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'var(--iab-text-lg)', fontStyle: 'italic', color: '#C9A24B' }}>
              I am because they were.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link
                to="/icons"
                data-testid="about-icons-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
                style={{ background: '#C99A2E', color: '#fff', fontFamily: 'var(--iab-font-primary)' }}
              >
                Meet the Icons <ArrowRight size={14} />
              </Link>
              <Link
                to="/because"
                data-testid="about-because-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
                style={{ background: 'rgba(201,154,46,0.15)', color: '#C99A2E', border: '1px solid rgba(201,154,46,0.3)', fontFamily: 'var(--iab-font-primary)' }}
              >
                Why this room exists
              </Link>
            </div>
          </div>

          <p style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-story)', lineHeight: 'var(--iab-leading-story)', color: 'var(--iab-charcoal)' }}>
            iambecause was born in 2026 from a simple belief &mdash; that if we understood why people do what they do, we would understand each other a little better. And the world needs that more than it needs anything else right now.
          </p>

          {/* Email */}
          <p>
            <a href="mailto:hello@iambecause.com" style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-sm)', color: '#E91E8C', fontWeight: 700, textDecoration: 'none' }}>
              hello@iambecause.com
            </a>
          </p>

          {/* Powered by Mira — small, centered, with pink orb icon */}
          <div className="flex items-center justify-center gap-2 pt-6">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#E91E8C' }}>
              <SparkleIcon size={11} className="text-white" />
            </div>
            <span style={{ fontFamily: 'var(--iab-font-primary)', fontSize: 'var(--iab-text-xs)', color: 'var(--iab-charcoal-pale)' }}>
              Powered by Mira.
            </span>
          </div>
        </div>

        {/* CTA — Talk to Mira */}
        <div className="mt-16 pt-10 text-center" style={{ borderTop: '0.5px solid var(--iab-border)' }}>
          <button
            data-testid="about-cta"
            onClick={() => {
              const orb = document.querySelector('[data-testid="mira-orb"]');
              if (orb) orb.click();
            }}
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-base font-sans font-semibold text-white transition-all hover:scale-105"
            style={{ background: '#E91E8C' }}
          >
            Talk to Mira
            <ArrowRight size={18} />
          </button>
          <p className="mt-3 text-center" style={{ fontFamily: 'var(--iab-font-primary)', fontSize: '11px', color: '#888780', fontStyle: 'italic' }}>
            Mira is the one who knows — she listens, she asks, she remembers.
          </p>
        </div>
      </div>
    </div>
  );
}
